import * as React from 'react';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {safelyGetFilterByKey} from 'nutshell-core/filter';
import type {FilterObject} from 'nutshell-core/types';

import {ExpandedFilterBar} from 'shells/control-bar/expanded-filter-bar';
import {getFormStatusesForQuery, getIgnoredValue} from 'shells/control-bar/helpers';
import {ListPageControlBar} from 'shells/control-bar/list-page-control-bar';
import {PaginatedView} from 'shells/list-view/paginated-view';

import type {McfxFormFragment as McfxForm} from 'nutshell-graphql-types';

import {writeLocalStorage} from '../../../util/localStorage';

import {AnalyticsStatusButton} from '../../email-marketing/analytics/report/analytics-status/analytics-status-button';
import {PageContainer, PageTitleBar} from '../../layout';
import {TitleBarLimits} from '../../limits/title-bar-limits';

import {McfxFormListQuery} from '../graphql/connected-forms-list-query';
import {getConnectedFormListQueryVariables} from '../graphql/helpers';

import {ConnectedFormsList} from './connected-forms-list';

const CONNECTED_FORMS_SEARCH = 'CONNECTED_FORMS_SEARCH';

type OwnProps = ContextRouter & {
    activeFilters: FilterObject[];
    onUpdateFilters: (arg1: FilterObject) => void;
    onRemoveAllFilters: () => void;
};

type Props = OwnProps & {
    mcfxForms: McfxForm[];
    totalCount: number;
    isLoading: boolean;
    error: string | null | undefined;
    refetch: () => Promise<any>;
    isFilteringOnIgnored: boolean;
    hasNextPage: boolean;
    onFetchMoreRows: () => any;
};

function ConnectedFormsListPageComponent(props: Props) {
    const title = 'Connected forms';

    React.useEffect(() => {
        // If the user removes all filters, the location.search becomes '?', which
        // results in the page not reloading. In this case, we will replace the search
        // with an empty string so the reload works as expected
        const search = props.location.search === '?' ? '' : props.location.search;
        writeLocalStorage(CONNECTED_FORMS_SEARCH, search);
    }, [props.location.search]);

    const isFiltering = Boolean(props.activeFilters.length);

    return (
        <PageContainer
            title={title}
            pageTitleBar={
                <PageTitleBar
                    title={title}
                    subheaderPrimaryText='Collect leads from any form on your website'
                    titleBadge={<TitleBarLimits type='FORMS' />}
                    buttons={<AnalyticsStatusButton pageType='connected-forms' />}
                />
            }
            controlBar={
                <ListPageControlBar
                    leftSection={
                        <ExpandedFilterBar
                            statusFilterProps={{
                                statusFilter: safelyGetFilterByKey(props.activeFilters, 'status'),
                                statusOptions: ['Draft', 'Active'],
                                statusDisabled: props.isFilteringOnIgnored,
                            }}
                            ignoredFilterProps={{
                                isFilteringOnIgnored: props.isFilteringOnIgnored,
                            }}
                            isFiltering={isFiltering}
                            noun='form'
                            count={props.totalCount}
                            onUpdateFilters={props.onUpdateFilters}
                            onRemoveAllFilters={props.onRemoveAllFilters}
                            // @ts-expect-error
                            activeFilters={props.activeFilters}
                        />
                    }
                />
            }
        >
            <PaginatedView
                hasNextPage={props.hasNextPage}
                isLoading={props.isLoading}
                onFetchMoreRows={props.onFetchMoreRows}
                renderBody={
                    <ConnectedFormsList
                        isLoading={props.isLoading}
                        mcfxForms={props.mcfxForms}
                        error={props.error}
                        isViewingIgnored={props.isFilteringOnIgnored}
                        isFiltering={isFiltering}
                        refetch={props.refetch}
                    />
                }
            />
        </PageContainer>
    );
}

export function ConnectedFormsListPage(props: OwnProps) {
    // Rough calculation of how many rows fit on the screen
    const limit = Math.ceil(window.innerHeight / 33);
    const isFilteringOnIgnored = getIgnoredValue(props.activeFilters);
    const statuses = getFormStatusesForQuery(props.activeFilters);
    const queryVariables = getConnectedFormListQueryVariables(
        isFilteringOnIgnored,
        statuses,
        limit
    );

    return (
        <McfxFormListQuery queryVariables={queryVariables} limit={limit}>
            {({mcfxForms, totalCount, isLoading, error, refetch, fetchMoreRows, hasNextPage}) => (
                <ConnectedFormsListPageComponent
                    {...props}
                    mcfxForms={mcfxForms}
                    totalCount={totalCount}
                    isLoading={isLoading}
                    error={error}
                    refetch={refetch}
                    onFetchMoreRows={fetchMoreRows}
                    hasNextPage={hasNextPage}
                    isFilteringOnIgnored={isFilteringOnIgnored}
                />
            )}
        </McfxFormListQuery>
    );
}
